import { resolveDesignProfile } from "./design-profiles";
import { intraLinks } from "./diagnostics/graph";
import { isAcCable } from "./ac";
import { computeBatteryBankCurrents } from "./battery-bank";
import { collectCrankingCircuits, computeCrankingCables, computeKnownStartingCurrentCables } from "./cranking-circuit";
import { computeCableSpec, isPassthroughComponent, type SimulateContext } from "./cable-current";
import { getDefinition, type DefLookup } from "./catalog";
import { computeCableDemands } from "./cable-demands";
import { collectChargerOutputCircuits, chargerCircuitAllowancePct } from "./charger-output-circuits";
import { sustainedCableCurrent } from "./cable-current-evidence";
import { collectLoadCircuits, type LoadCircuit } from "./load-circuits";
import { isJunction } from "./node-kind";
import { indexNodesById } from "./node-tree";
import { computePvCurrentAnalysis } from "./pv-currents";
import { measure } from "./perf-marks";
import type { System } from "./types";
import { computeVdropAllowances } from "./vdrop-allowance";
import { DEFAULT_NOMINAL_V } from "./voltage-domain";
import { VDROP_PCT_CRITICAL, voltageDrop } from "./wire-tables";

/**
 * Groups cables into contiguous series runs sized as a unit: a passthrough node
 * (junction, busbar, switch, shunt) with exactly 2 cables joins them, 3 or more
 * is a branch point. A chassis-ground symbol never joins — every symbol is one
 * electrical point, so two cables meeting on it are parallel returns, not a
 * run; treating them as one sized an alternator's ground strap after the
 * engine's. Returns `cable.id → canonical group id`.
 */
export function buildSeriesGroups(
  system: System,
  defLookup: DefLookup,
): Map<string, string> {
  const parent = new Map<string, string>();
  for (const c of system.cables) parent.set(c.id, c.id);
  const find = (x: string): string => {
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r)!;
    let cur = x;
    while (parent.get(cur) !== r) {
      const next = parent.get(cur)!;
      parent.set(cur, r);
      cur = next;
    }
    return r;
  };
  const union = (a: string, b: string): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  const cablesById = new Map(system.cables.map(c => [c.id, c]));
  const cablesByNode = new Map<string, string[]>();
  for (const c of system.cables) {
    (cablesByNode.get(c.fromNodeId) ?? cablesByNode.set(c.fromNodeId, []).get(c.fromNodeId)!).push(c.id);
    (cablesByNode.get(c.toNodeId) ?? cablesByNode.set(c.toNodeId, []).get(c.toNodeId)!).push(c.id);
  }

  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (def?.renderVariant === "chassis-ground") continue;
    const passthrough = isJunction(node, def) || (def != null && isPassthroughComponent(def));
    if (!passthrough) continue;
    const incident = cablesByNode.get(node.id) ?? [];
    if (incident.length !== 2 || !def) continue;
    const terminals = incident.map(id => {
      const c = cablesById.get(id)!;
      return c.fromNodeId === node.id ? c.fromTerminalId : c.toTerminalId;
    });
    // Two cables touching one enclosure need not be in series: a panel has
    // independent positive/negative rails and a selector has isolated posts.
    const links = intraLinks(node, def, system.cables);
    const reached = new Set([terminals[0]]);
    const pending = [terminals[0]];
    for (let i = 0; i < pending.length; i++) {
      for (const link of links) {
        const next = link.from === pending[i] ? link.to : link.to === pending[i] ? link.from : undefined;
        if (next == null || reached.has(next)) continue;
        reached.add(next);
        pending.push(next);
      }
    }
    if (reached.has(terminals[1])) union(incident[0], incident[1]);
  }

  const groups = new Map<string, string>();
  for (const c of system.cables) groups.set(c.id, find(c.id));
  return groups;
}

/** Keyed by (System, definition lookup, simulate, targetPct) — a fresh System per mutation drops stale entries. */
const pathMinCache = new WeakMap<
  System,
  WeakMap<DefLookup, Map<SimulateContext | undefined, Map<number, Map<string, number>>>>
>();

/**
 * Per cable, the minimum mm² that keeps every load's circuit inside its own
 * ABYC E-11.14.2.6 allowance, measured the way the clause does: out along the
 * feed and back along the return, at the circuit's own nominal voltage. An
 * absent entry means the per-cable auto pick already meets it. Starter loops
 * use their 2% complete-circuit allowance at peak current. Supported PV strings
 * use the selected planning target over all copper at the full string voltage.
 * A cable outside
 * these circuits can still match its partner across a two-cable passthrough
 * (a switch run is one gauge end to end).
 */
export function computePathMinWireSizes(
  system: System,
  defLookup: DefLookup = getDefinition,
  simulate?: SimulateContext,
  /** Planning target (%) for PV and unclassified loads; classified loads
   *  use their own ABYC E-11.14.2.6 allowance. */
  targetPct = VDROP_PCT_CRITICAL,
): Map<string, number> {
  let byDefinition = pathMinCache.get(system);
  if (!byDefinition) {
    byDefinition = new WeakMap();
    pathMinCache.set(system, byDefinition);
  }
  let bySim = byDefinition.get(defLookup);
  if (!bySim) {
    bySim = new Map();
    byDefinition.set(defLookup, bySim);
  }
  let byPct = bySim.get(simulate);
  if (!byPct) {
    byPct = new Map();
    bySim.set(simulate, byPct);
  }
  const cached = byPct.get(targetPct);
  if (cached) return cached;
  const result = measure("computePathMinWireSizes", () =>
    computePathMinWireSizesImpl(system, defLookup, simulate, targetPct),
  );
  byPct.set(targetPct, result);
  return result;
}

type BudgetedCircuit = LoadCircuit & {
  targetV: number;
  cranking: boolean;
  charging: boolean;
  /** `voltageDrop` is out-and-back for a one-way length. With both legs drawn
   *  each conductor is one direction of that loop and counts at half; with only
   *  the feed drawn the return is assumed to mirror it. */
  weight: number;
};

function computePathMinWireSizesImpl(
  system: System,
  defLookup: DefLookup = getDefinition,
  simulate?: SimulateContext,
  targetPct = VDROP_PCT_CRITICAL,
): Map<string, number> {
  const result = new Map<string, number>();
  const profile = resolveDesignProfile(system);
  const wireTable = profile.policy.wireTable;
  const starterCircuits = collectCrankingCircuits(system, defLookup);
  const starterVertices = new Set(starterCircuits.map(c => c.loadVertex));
  const otherCircuits = [...collectLoadCircuits(system, defLookup), ...starterCircuits];
  const chargerCircuits = collectChargerOutputCircuits(system, defLookup);
  const charging = new Map<LoadCircuit, typeof chargerCircuits[number]>(chargerCircuits.map(c => [c, c]));
  const nodesById = indexNodesById(system.nodes);
  const pvCircuits = computePvCurrentAnalysis(nodesById, system.cables, defLookup).circuits;
  const pv = new Map<LoadCircuit, typeof pvCircuits[number]>(pvCircuits.map(c => [c, c]));
  const circuits = [...otherCircuits, ...chargerCircuits, ...pvCircuits];
  if (circuits.length === 0) return result;

  const demands = computeCableDemands(system, defLookup, simulate);
  const allowances = computeVdropAllowances(system, defLookup, targetPct);
  const bank = computeBatteryBankCurrents(system, defLookup, simulate);
  const cranking = computeCrankingCables(system, defLookup);
  const startingEvidence = computeKnownStartingCurrentCables(system, defLookup);

  const initialMm2 = new Map<string, number>();
  const chosen = new Map<string, number>();
  const cableLength = new Map<string, number>();
  const cableContinuous = new Map<string, number>();
  const cablePeak = new Map<string, number>();
  const cableSustained = new Map<string, number>();
  const unresolvedCharging = new Set<string>();
  const cableNominalV = new Map<string, number>();
  const overrideMm2 = new Map<string, number>();
  const acCableIds = new Set<string>();
  for (const c of system.cables) {
    if (isAcCable(c)) acCableIds.add(c.id);
    const bankCurrent = bank.byCable.get(c.id);
    const spec = computeCableSpec(
      c,
      nodesById,
      system.cables,
      defLookup,
      simulate,
      demands.get(c.id),
      undefined,
      cranking.has(c.id),
      allowances.byCable.get(c.id),
      bankCurrent == null ? undefined : Math.abs(bankCurrent),
      startingEvidence.has(c.id),
      undefined,
      0,
      profile,
    );
    initialMm2.set(c.id, spec.wire.mm2);
    chosen.set(c.id, spec.wire.mm2);
    cableLength.set(c.id, c.lengthM);
    // An AC run has no DC voltage drop to budget and is never bumped for one.
    cableContinuous.set(c.id, spec.isAc ? 0 : spec.continuous);
    cablePeak.set(c.id, spec.isAc ? 0 : spec.current);
    cableSustained.set(c.id, spec.isAc ? 0 : sustainedCableCurrent(spec));
    // Complete source-sharing bounds remain valid conservative design inputs.
    // Provisional current cannot certify an automatic charging-loop budget.
    if (!simulate && (spec.currentBasis !== "complete-route-envelope" || spec.sustainedCurrent == null)) unresolvedCharging.add(c.id);
    cableNominalV.set(c.id, spec.nominalV);
    if (c.wireSizeMm2Override != null) overrideMm2.set(c.id, c.wireSizeMm2Override);
  }

  const supportedChargingCables = new Set([...chargerCircuits, ...pvCircuits]
    .filter(c => [...c.pos, ...c.neg].every(id => !unresolvedCharging.has(id) && !acCableIds.has(id)))
    .flatMap(c => [...c.pos, ...c.neg]));
  // Discovering only unresolved charger routes must not activate unrelated
  // wire matching in a drawing that previously had no budgetable circuit.
  if (!otherCircuits.length && !supportedChargingCables.size) return result;

  // A series run must not be part 10 mm² and part 25 mm² across a switch.
  const seriesGroups = buildSeriesGroups(system, defLookup);
  const groupMembers = new Map<string, string[]>();
  for (const [cid, root] of seriesGroups) {
    const arr = groupMembers.get(root) ?? [];
    arr.push(cid);
    groupMembers.set(root, arr);
  }
  const membersOf = (id: string): string[] =>
    groupMembers.get(seriesGroups.get(id) ?? id) ?? [id];
  const normalizeGroup = (root: string): void => {
    const members = groupMembers.get(root);
    if (!members || members.length < 2) return;
    if (!otherCircuits.length && !members.some(id => supportedChargingCables.has(id))) return;
    let maxMm2 = 0;
    for (const id of members) {
      const cur = chosen.get(id) ?? 0;
      if (cur > maxMm2) maxMm2 = cur;
    }
    for (const id of members) {
      if (overrideMm2.has(id)) continue;
      const cur = chosen.get(id) ?? 0;
      if (cur < maxMm2) chosen.set(id, maxMm2);
    }
  };
  for (const root of groupMembers.keys()) normalizeGroup(root);
  // What each cable is before any voltage-drop bump: its own pick, lifted to
  // its series run. The floor nothing below relaxes past.
  const baseline = new Map(chosen);
  const raiseTo = (id: string, mm2: number): void => {
    if (overrideMm2.has(id)) return;
    if ((chosen.get(id) ?? 0) >= mm2) return;
    chosen.set(id, mm2);
    normalizeGroup(seriesGroups.get(id) ?? id);
  };

  const MAX_MM2 = wireTable[wireTable.length - 1].mm2;
  const sizeOf = (id: string): number => chosen.get(id) ?? 0;
  const currents = (c: Pick<BudgetedCircuit, "cranking" | "charging">): Map<string, number> =>
    c.cranking ? cablePeak : c.charging ? cableSustained : cableContinuous;
  const dropAt = (id: string, mm2: number, circuit: BudgetedCircuit): number =>
    voltageDrop(currents(circuit).get(id) ?? 0, cableLength.get(id) ?? 0, mm2);
  const legDrop = (ids: string[], size: (id: string) => number, circuit: BudgetedCircuit): number => {
    let s = 0;
    for (const id of ids) s += dropAt(id, size(id), circuit);
    return s;
  };
  const circuitDrop = (c: BudgetedCircuit, size: (id: string) => number): number =>
    c.weight * (legDrop(c.pos, size, c) + (c.neg.length > 0 ? legDrop(c.neg, size, c) : 0));
  const nextStep = (id: string): number | undefined => {
    const idx = wireTable.findIndex((w) => w.mm2 === sizeOf(id));
    if (idx < 0 || idx >= wireTable.length - 1) return undefined;
    return wireTable[idx + 1].mm2;
  };

  const budgeted: BudgetedCircuit[] = [];
  const beyondTable = new Set<string>();
  for (const c of circuits) {
    // A circuit drawn through an AC conductor (a PE bonded to DC negative)
    // is not one the DC budget can measure.
    if ([...c.pos, ...c.neg].some((id) => acCableIds.has(id))) continue;
    const charger = charging.get(c);
    const pvCircuit = pv.get(c);
    if ((charger || pvCircuit) && [...c.pos, ...c.neg].some(id => unresolvedCharging.has(id))) continue;
    const nominalV = pvCircuit?.nominalV ?? charger?.nominalV ?? cableNominalV.get(c.pos[0]) ?? DEFAULT_NOMINAL_V;
    const isCranking = starterVertices.has(c.loadVertex);
    // Apply the planner starting target at peak current; charger routes use
    // their own manufacturer or selected full-loop allowance.
    const pct = pvCircuit ? Math.min(targetPct, ...c.pos.map(id => allowances.byCable.get(id) ?? targetPct))
      : charger ? chargerCircuitAllowancePct(charger, targetPct)
      : isCranking ? profile.policy.voltageDrop.startingPct : allowances.byLoadVertex.get(c.loadVertex) ?? targetPct;
    const circuit: BudgetedCircuit = {
      ...c,
      targetV: (nominalV * pct) / 100,
      cranking: isCranking,
      charging: charger != null || pvCircuit != null,
      weight: c.complete ? 0.5 : 1,
    };
    // When even the largest wire in the table misses the target, bumping drives
    // unlocked cables to 120 mm² and pollutes sibling circuits' shared trunks.
    const bestCase = (id: string): number =>
      overrideMm2.has(id) ? sizeOf(id) : MAX_MM2;
    if (circuitDrop(circuit, bestCase) > circuit.targetV) {
      // A bounded profile cannot certify a saturated path even when each
      // short segment separately meets its target. A floor above the table
      // carries that unavailable result to the shared cable-spec boundary.
      if (profile.policy.coverage.enforceTableCeiling && circuitDrop(circuit, () => MAX_MM2) > circuit.targetV) {
        for (const id of [...c.pos, ...c.neg]) beyondTable.add(id);
      }
      continue;
    }
    budgeted.push(circuit);
  }

  // One table step per iteration, on the cable that buys the most drop summed
  // over every circuit still over budget. A trunk's gain counts once per circuit
  // through it, so a feeder shared by ten loads grows before any one load's
  // branch does — the branch is where a load's own drop is, the feeder is where
  // everybody's is.
  const maxIterations = wireTable.length * system.cables.length + 1;
  for (let iter = 0; iter < maxIterations; iter++) {
    const over = budgeted.filter((c) => circuitDrop(c, sizeOf) > c.targetV);
    if (over.length === 0) break;
    const gains = new Map<string, number>();
    for (const c of over) {
      for (const id of c.neg.length > 0 ? [...c.pos, ...c.neg] : c.pos) {
        if (overrideMm2.has(id)) continue;
        const next = nextStep(id);
        if (next === undefined) continue;
        if ((currents(c).get(id) ?? 0) <= 0 || (cableLength.get(id) ?? 0) <= 0) continue;
        const gain = c.weight * (dropAt(id, sizeOf(id), c) - dropAt(id, next, c));
        gains.set(id, (gains.get(id) ?? 0) + gain);
      }
    }
    let bestId: string | null = null;
    let bestGain = 0;
    for (const [id, gain] of gains) {
      if (gain > bestGain) {
        bestGain = gain;
        bestId = id;
      }
    }
    if (!bestId) break;
    chosen.set(bestId, nextStep(bestId)!);
    normalizeGroup(seriesGroups.get(bestId) ?? bestId);
  }

  const stepBelow = (mm2: number): number => {
    const idx = wireTable.findIndex((w) => w.mm2 === mm2);
    return idx > 0 ? wireTable[idx - 1].mm2 : mm2;
  };
  const stepsApart = (a: string, b: string): number =>
    Math.abs(
      wireTable.findIndex((w) => w.mm2 === sizeOf(a)) -
        wireTable.findIndex((w) => w.mm2 === sizeOf(b)),
    );

  // A load's feed and return carry the same current over the same route and
  // are one gauge on the boat; budgeting only the feed left the return at the
  // table minimum while the feed grew four sizes. When the thicker leg grew for
  // voltage drop the other follows it. When it is thick for a reason of its
  // own — an OCPD on the ungrounded leg wants ampacity the return never needs
  // (E-11.10.1 fuses the ungrounded conductor only) — the other leg comes to
  // within one table step and no further.
  for (const c of budgeted) {
    // Charging paths can traverse shared starting returns and independent
    // source branches. A neighbouring conductor's size is not their demand.
    if (c.charging || c.neg.length === 0) continue;
    const [thick, thin] =
      sizeOf(c.pos[0]) >= sizeOf(c.neg[0]) ? [c.pos[0], c.neg[0]] : [c.neg[0], c.pos[0]];
    const grewForVdrop = sizeOf(thick) > (baseline.get(thick) ?? 0);
    raiseTo(thin, grewForVdrop ? sizeOf(thick) : stepBelow(sizeOf(thick)));
  }

  // An upstream cable carries every amp downstream of it, so it can't be
  // thinner than what it feeds — the gain-driven loop can leave it that way.
  // A user-pinned override caps the propagation instead of being overwritten.
  for (const c of budgeted) {
    if (c.charging) continue;
    for (const leg of [c.pos, c.neg]) {
      let runningMax = 0;
      for (const id of leg) {
        const cur = sizeOf(id);
        if (cur > runningMax) {
          runningMax = cur;
          continue;
        }
        if (cur === runningMax) continue;
        if (overrideMm2.has(id)) {
          runningMax = cur;
          continue;
        }
        chosen.set(id, runningMax);
        normalizeGroup(seriesGroups.get(id) ?? id);
      }
    }
  }

  // The loop climbs a load's own legs before the trunk it shares has grown for
  // its siblings; once the trunk has settled the legs can often come back
  // down. Only legs on no other circuit are relaxed — anything else is
  // somebody's trunk. Both legs step together; a leg already at its own pick
  // stays, and the pair never drifts past one step apart.
  const circuitsPerCable = new Map<string, number>();
  for (const c of circuits) {
    for (const id of [...c.pos, ...c.neg]) {
      circuitsPerCable.set(id, (circuitsPerCable.get(id) ?? 0) + 1);
    }
  }
  for (const c of budgeted) {
    const legs = c.neg.length > 0 ? [c.pos[0], c.neg[0]] : [c.pos[0]];
    const members = new Set<string>();
    for (const leg of legs) for (const m of membersOf(leg)) members.add(m);
    let relaxable = true;
    for (const m of members) {
      if (overrideMm2.has(m) || circuitsPerCable.get(m) !== 1) relaxable = false;
    }
    if (!relaxable) continue;
    for (;;) {
      const trialSizes = new Map<string, number>();
      for (const m of members) {
        const down = stepBelow(sizeOf(m));
        if (down < sizeOf(m) && down >= (baseline.get(m) ?? 0)) trialSizes.set(m, down);
      }
      if (trialSizes.size === 0) break;
      const trial = (id: string): number => trialSizes.get(id) ?? sizeOf(id);
      if (circuitDrop(c, trial) > c.targetV) break;
      if (!c.charging && legs.length === 2) {
        const a = wireTable.findIndex((w) => w.mm2 === trial(legs[0]));
        const b = wireTable.findIndex((w) => w.mm2 === trial(legs[1]));
        if (Math.abs(a - b) > 1) break;
      }
      for (const [m, mm2] of trialSizes) chosen.set(m, mm2);
    }
    if (!c.charging && legs.length === 2 && stepsApart(legs[0], legs[1]) > 1) {
      // Unreachable by construction; keeps the invariant explicit.
      const m = Math.max(sizeOf(legs[0]), sizeOf(legs[1]));
      raiseTo(legs[0], stepBelow(m));
      raiseTo(legs[1], stepBelow(m));
    }
  }

  for (const c of system.cables) {
    const initial = initialMm2.get(c.id) ?? 0;
    const final = chosen.get(c.id) ?? initial;
    if (final > initial) result.set(c.id, final);
  }
  for (const id of beyondTable) result.set(id, MAX_MM2 + 1);
  return result;
}
